import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import Sidebar from '../components/(admin)/Sidebar'
import PageTopBar from '../components/(admin)/PageTopBar'
import { colors } from '../constants/colors'
import NotificationModal from '../components/NotificationModal'
import { setBusinessLogo, setBusinessName, setHumanChatEnabled, fetchProfile } from '../store/slices/authSlice'
import { updateBusiness, updateHumanAgentEnabled } from '../services/(adminapi)/messagesAPI'
import { mediaAPI } from '../services/(adminapi)/mediaAPI'
import { resolveMediaUrl } from '../utils/mediaUrl'

const Settings = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user, business } = useSelector((s) => s.auth)

  const logoRef = useRef(null)
  const [businessName, setBusinessNameLocal] = useState(business?.name ?? '')
  const [savingName, setSavingName] = useState(false)
  const [uploadingLogo, setUploadingLogo] = useState(false)
  const [isUpdatingHumanAgent, setIsUpdatingHumanAgent] = useState(false)
  const [successMessage, setSuccessMessage] = useState(null)

  useEffect(() => {
    dispatch(fetchProfile())
  }, [dispatch])

  useEffect(() => {
    setBusinessNameLocal(business?.name ?? '')
  }, [business?.name])

  const isOwner = user?.role === 'Owner'

  const handleSaveBusinessName = async () => {
    const trimmed = (businessName || '').trim()
    if (!trimmed || trimmed === (business?.name ?? '')) return
    if (!business?.id) return

    setSavingName(true)
    try {
      await updateBusiness(business.id, { name: trimmed })
      dispatch(setBusinessName(trimmed))
      setSuccessMessage('Business name updated successfully')
    } catch (e) {
      console.error('Failed to update business name', e)
    } finally {
      setSavingName(false)
    }
  }

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0]
    if (!file?.type.startsWith('image/')) return

    setUploadingLogo(true)
    const formData = new FormData()
    formData.append('file', file)
    mediaAPI
      .uploadBusinessLogo(formData)
      .then((res) => {
        const url = res.data?.data?.url || res.data?.data?.logo
        if (url) {
          dispatch(setBusinessLogo(`${resolveMediaUrl(url)}?t=${Date.now()}`))
          setSuccessMessage('Business logo updated successfully')
        }
      })
      .catch((err) => console.error('Business logo update failed:', err))
      .finally(() => {
        setUploadingLogo(false)
        e.target.value = ''
        logoRef.current && (logoRef.current.value = '')
      })
  }

  const handleHumanAgentToggle = async (event) => {
    const nextValue = event.target.checked
    setIsUpdatingHumanAgent(true)

    try {
      await updateHumanAgentEnabled(nextValue)
      dispatch(setHumanChatEnabled(nextValue))
      setSuccessMessage(`Human agent handoff ${nextValue ? 'enabled' : 'disabled'} successfully`)
    } catch (error) {
      console.error('Failed to update human agent status:', error)
    } finally {
      setIsUpdatingHumanAgent(false)
    }
  }

  const cardStyle = {
    backgroundColor: colors.background.primary,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: '8px',
    padding: '20px',
  }
  const titleStyle = {
    fontSize: '16px',
    fontWeight: 700,
    color: colors.text.primary,
    margin: 0,
    marginBottom: 6,
  }
  const subtitleStyle = {
    fontSize: '13px',
    color: colors.text.secondary,
    margin: 0,
    marginBottom: 16,
  }
  const labelStyle = {
    fontSize: 13,
    fontWeight: 500,
    color: colors.text.secondary,
    marginBottom: 6,
    display: 'block',
  }
  const inputStyle = {
    width: '100%',
    maxWidth: 520,
    padding: '10px 12px',
    fontSize: 14,
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: 6,
    color: colors.text.primary,
    boxSizing: 'border-box',
  }
  const btnStyle = {
    padding: '8px 16px',
    fontSize: 14,
    fontWeight: 600,
    color: '#fff',
    backgroundColor: colors.primary,
    border: 'none',
    borderRadius: 6,
    cursor: 'pointer',
  }
  const btnSecondaryStyle = {
    ...btnStyle,
    backgroundColor: colors.background.primary,
    color: colors.text.primary,
    border: `1px solid ${colors.borderVariants.light}`,
  }
  const btnDisabledStyle = {
    opacity: 0.6,
    cursor: 'not-allowed',
  }
  const backBtnStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 12px',
    fontSize: 14,
    fontWeight: 500,
    color: colors.text.primary,
    backgroundColor: 'transparent',
    border: `1px solid ${colors.borderVariants.light}`,
    borderRadius: 6,
    cursor: 'pointer',
  }
  const logoWrap = {
    width: 80,
    height: 80,
    borderRadius: 8,
    overflow: 'hidden',
    backgroundColor: colors.background.tertiary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: colors.text.light,
    border: `1px solid ${colors.borderVariants.light}`,
  }

  return (
    <div style={{ minHeight: '100vh', display: 'flex', backgroundColor: colors.background.secondary }}>
      <NotificationModal
        isOpen={!!successMessage}
        onClose={() => setSuccessMessage(null)}
        type="success"
        title="Success"
        message={successMessage || ''}
        showOverlay={false}
      />
      <Sidebar />
      <div style={{ flex: 1, marginLeft: '280px', padding: '24px' }}>
        <PageTopBar
          title="Business Settings"
          onAiConfig={() => navigate('/ai-configuration')}
          onEmbeddingGuide={() => navigate('/embedding-guide')}
        />

        <button type="button" style={{ ...backBtnStyle, marginBottom: 16 }} onClick={() => navigate(-1)} aria-label="Go back">
          <svg width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path d="M19 12H5M12 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Back
        </button>

        {!isOwner ? (
          <div style={{ ...cardStyle, color: colors.text.secondary }}>
            You don’t have permission to view business settings. Please contact the business owner.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 18, maxWidth: 920 }}>
            <div style={cardStyle}>
              <h3 style={titleStyle}>Business details</h3>
              <p style={subtitleStyle}>Update your business name and branding.</p>

              <label style={labelStyle}>Business name</label>
              <input
                value={businessName}
                onChange={(e) => setBusinessNameLocal(e.target.value)}
                style={inputStyle}
                placeholder="Enter business name"
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleSaveBusinessName}
                  disabled={savingName || !businessName.trim() || businessName.trim() === (business?.name ?? '')}
                  style={{
                    ...btnStyle,
                    ...(savingName || !businessName.trim() || businessName.trim() === (business?.name ?? '') ? btnDisabledStyle : null),
                  }}
                >
                  {savingName ? 'Saving…' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => setBusinessNameLocal(business?.name ?? '')}
                  disabled={savingName || businessName === (business?.name ?? '')}
                  style={{
                    ...btnSecondaryStyle,
                    ...(savingName || businessName === (business?.name ?? '') ? btnDisabledStyle : null),
                  }}
                >
                  Reset
                </button>
              </div>

              <div style={{ marginTop: 18 }}>
                <label style={labelStyle}>Business logo</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                  <div style={logoWrap}>
                    {business?.logo ? (
                      <img src={resolveMediaUrl(business.logo)} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                      <span style={{ fontSize: 12, color: colors.text.secondary }}>No logo</span>
                    )}
                  </div>
                  <input
                    ref={logoRef}
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                    style={{ display: 'none' }}
                  />
                  <button
                    type="button"
                    onClick={() => logoRef.current?.click()}
                    disabled={uploadingLogo}
                    style={{
                      ...btnSecondaryStyle,
                      ...(uploadingLogo ? btnDisabledStyle : null),
                    }}
                  >
                    {uploadingLogo ? 'Uploading…' : 'Change logo'}
                  </button>
                </div>
              </div>
            </div>

            <div style={cardStyle}>
              <h3 style={titleStyle}>Human agent handoff</h3>
              <p style={subtitleStyle}>Allow handoff to human support agents.</p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: isUpdatingHumanAgent ? 'not-allowed' : 'pointer' }}>
                <input
                  type="checkbox"
                  checked={Boolean(business?.human_agent_enabled)}
                  onChange={handleHumanAgentToggle}
                  disabled={isUpdatingHumanAgent}
                  style={{ width: 18, height: 18, cursor: isUpdatingHumanAgent ? 'not-allowed' : 'pointer' }}
                />
                <span style={{ color: colors.text.secondary, fontSize: 13 }}>
                  {isUpdatingHumanAgent ? 'Updating…' : business?.human_agent_enabled ? 'Enabled' : 'Disabled'}
                </span>
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Settings

